import { useState } from 'react'
import { Modal } from '@/components/Modal'
import { SearchableSelect } from '@/components/SearchableSelect'
import { optionsFrom, useDegrees, useMajors, useUniversities } from './queries'
import { joinMonthValue, splitMonthValue, type EducationRow } from './types'

/**
 * Port of Views/Profile/Information/_Education.cshtml + _education.js.
 *
 * The Razor had a `#tblEducation` table commented out and the rows lived only in
 * an in-memory `Education.Collection` rendered by JS. The table is restored here
 * because rows are addable and removable and the candidate otherwise cannot see
 * what they have entered — the JS was already rendering exactly this.
 *
 * The "Other" checkboxes swap the University / Major dropdown for a free-text
 * box and send id 0 with the text in `UniversityOther` / `MajorOther`, which is
 * what SaveAll expected.
 */
interface Props {
  rows: EducationRow[]
  onChange: (rows: EducationRow[]) => void
  showErrors: boolean
  /** The "Add" button lives in the section header, which the page owns. */
  isModalOpen: boolean
  onCloseModal: () => void
}

const EMPTY: EducationRow = {
  mstDegreeID: '',
  DegreeName: '',
  mstUniversityID: '',
  UniversityName: '',
  UniversityOther: '',
  mstMajorID: '',
  MajorName: '',
  MajorOther: '',
  GPA: '',
  MonthIn: '',
  YearIn: '',
  MonthOut: '',
  YearOut: '',
  isUntilNow: false,
}

export function EducationSection({
  rows,
  onChange,
  showErrors,
  isModalOpen,
  onCloseModal,
}: Props): JSX.Element {
  const [draft, setDraft] = useState<EducationRow>(EMPTY)
  const [otherUniversity, setOtherUniversity] = useState(false)
  const [otherMajor, setOtherMajor] = useState(false)
  const [error, setError] = useState('')

  const degrees = useDegrees()
  const universities = useUniversities()
  const majors = useMajors()

  function add(): void {
    if (!draft.mstDegreeID) return setError('Please choose a degree.')
    if (!otherUniversity && !draft.mstUniversityID) return setError('Please choose a university.')
    if (otherUniversity && !draft.UniversityOther.trim()) {
      return setError('Please type the university name.')
    }
    if (!otherMajor && !draft.mstMajorID) return setError('Please choose a major.')
    if (otherMajor && !draft.MajorOther.trim()) return setError('Please type the major.')
    if (!draft.GPA.trim()) return setError('Please enter your GPA.')
    if (!draft.YearIn) return setError('Please choose the start period.')
    if (!draft.isUntilNow && !draft.YearOut) return setError('Please choose the end period.')

    setError('')
    onChange([
      ...rows,
      {
        ...draft,
        // "Other" means no master-data id: 0 with the free text alongside.
        mstUniversityID: otherUniversity ? '0' : draft.mstUniversityID,
        UniversityOther: otherUniversity ? draft.UniversityOther : '',
        UniversityName: otherUniversity ? draft.UniversityOther : draft.UniversityName,
        mstMajorID: otherMajor ? '0' : draft.mstMajorID,
        MajorOther: otherMajor ? draft.MajorOther : '',
        MajorName: otherMajor ? draft.MajorOther : draft.MajorName,
      },
    ])
    setDraft(EMPTY)
    setOtherUniversity(false)
    setOtherMajor(false)
    onCloseModal()
  }

  return (
    <>
      <div className="table-responsive">
        <table className="table table-hover" id="tblEducation">
          <thead>
            <tr>
              <th className="text-center">#</th>
              <th className="text-center">University</th>
              <th className="text-center">Degree</th>
              <th className="text-center">Major</th>
              <th className="text-center">Year In</th>
              <th className="text-center">Year Out</th>
              <th className="text-center" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center text-muted">
                  No education added yet
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={`${row.UniversityName}-${row.YearIn}-${index}`}>
                  <td className="text-center">{index + 1}</td>
                  <td>{row.UniversityOther || row.UniversityName}</td>
                  <td>{row.DegreeName}</td>
                  <td>{row.MajorOther || row.MajorName}</td>
                  <td className="text-center">{row.YearIn}</td>
                  <td className="text-center">{row.isUntilNow ? 'Until Now' : row.YearOut}</td>
                  <td className="text-center">
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-danger"
                      onClick={() => onChange(rows.filter((_, i) => i !== index))}
                    >
                      <i className="ti ti-trash" />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showErrors && rows.length === 0 && (
        <div className="alert alert-warning" role="alert" id="educationMessageWarning">
          Please fill in your education data or click the add button if you have already entered it.
        </div>
      )}

      <Modal
        id="educationModal"
        title="Add Education"
        isOpen={isModalOpen}
        onClose={onCloseModal}
      >
        <form className="needs-validation" id="addEducationInfo" noValidate>
        <div className="mb-3">
          <label className="form-label">
            Degree <span className="required">*</span>
          </label>
          <SearchableSelect
            id="slsDegree"
            options={optionsFrom(
              degrees.data?.Data,
              (d) => d.mstDegreeID,
              (d) => d.DegreeName,
            )}
            value={draft.mstDegreeID}
            onChange={(id) =>
              setDraft({
                ...draft,
                mstDegreeID: id,
                DegreeName:
                  degrees.data?.Data?.find((d) => String(d.mstDegreeID) === id)?.DegreeName ?? '',
              })
            }
            placeholder="Select degree"
            loading={degrees.isLoading}
            emptyLabel={null}
          />
        </div>

        <div className="row mb-3">
          <label className="form-label">
            University <span className="required">*</span>
          </label>
          <div className="col-sm-11">
            {otherUniversity ? (
              <input
                type="text"
                className="form-control"
                id="txtUniversityOther"
                maxLength={100}
                placeholder="University name"
                value={draft.UniversityOther}
                onChange={(e) => setDraft({ ...draft, UniversityOther: e.target.value })}
              />
            ) : (
              <SearchableSelect
                id="slsUniversity"
                options={optionsFrom(
                  universities.data?.Data,
                  (u) => u.mstUniversityID,
                  (u) => u.UniversityName,
                )}
                value={draft.mstUniversityID}
                onChange={(id) =>
                  setDraft({
                    ...draft,
                    mstUniversityID: id,
                    UniversityName:
                      universities.data?.Data?.find((u) => String(u.mstUniversityID) === id)
                        ?.UniversityName ?? '',
                  })
                }
                placeholder="Select university"
                loading={universities.isLoading}
                emptyLabel={null}
              />
            )}
          </div>
          <div className="form-check col-sm-1 mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="cbxOtherUniversity"
              checked={otherUniversity}
              onChange={(e) => setOtherUniversity(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="cbxOtherUniversity">
              {' '}
              Other{' '}
            </label>
          </div>
        </div>

        <div className="row mb-3">
          <label className="form-label">
            Major <span className="required">*</span>
          </label>
          <div className="col-sm-11">
            {otherMajor ? (
              <input
                type="text"
                className="form-control"
                id="txtMajorOther"
                maxLength={100}
                placeholder="Major"
                value={draft.MajorOther}
                onChange={(e) => setDraft({ ...draft, MajorOther: e.target.value })}
              />
            ) : (
              <SearchableSelect
                id="slsMajor"
                options={optionsFrom(
                  majors.data?.Data,
                  (m) => m.mstMajorID,
                  (m) => m.MajorName,
                )}
                value={draft.mstMajorID}
                onChange={(id) =>
                  setDraft({
                    ...draft,
                    mstMajorID: id,
                    MajorName:
                      majors.data?.Data?.find((m) => String(m.mstMajorID) === id)?.MajorName ?? '',
                  })
                }
                placeholder="Select major"
                loading={majors.isLoading}
                emptyLabel={null}
              />
            )}
          </div>
          <div className="form-check col-sm-1 mt-3">
            <input
              className="form-check-input"
              type="checkbox"
              id="cbxOtherMajor"
              checked={otherMajor}
              onChange={(e) => setOtherMajor(e.target.checked)}
            />
            <label className="form-check-label" htmlFor="cbxOtherMajor">
              {' '}
              Other{' '}
            </label>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">
            GPA <span className="required">*</span>
          </label>
          <input
            type="text"
            inputMode="decimal"
            className="form-control"
            id="txtGPA"
            placeholder="4.00"
            value={draft.GPA}
            onChange={(e) => setDraft({ ...draft, GPA: e.target.value })}
          />
        </div>

        <div className="row">
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Year In <span className="required">*</span>
            </label>
            <input
              type="month"
              className="form-control"
              id="dpEducationStart"
              value={joinMonthValue(
                draft.YearIn ? Number(draft.YearIn) : null,
                draft.MonthIn ? Number(draft.MonthIn) : null,
              )}
              onChange={(e) => {
                const { month, year } = splitMonthValue(e.target.value)
                setDraft({ ...draft, MonthIn: month ? String(month) : '', YearIn: year ? String(year) : '' })
              }}
            />
          </div>
          <div className="mb-3 col-md-6">
            <label className="form-label">
              Year Out <span className="required">*</span>
            </label>
            <input
              type="month"
              className="form-control"
              id="dpEducationEnd"
              disabled={draft.isUntilNow}
              value={joinMonthValue(
                draft.YearOut ? Number(draft.YearOut) : null,
                draft.MonthOut ? Number(draft.MonthOut) : null,
              )}
              onChange={(e) => {
                const { month, year } = splitMonthValue(e.target.value)
                setDraft({
                  ...draft,
                  MonthOut: month ? String(month) : '',
                  YearOut: year ? String(year) : '',
                })
              }}
            />
          </div>
        </div>

        <div className="row">
          <div className="mb-3 col-md-6" />
          <div className="mb-3 col-md-6">
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="isUntilNow"
                checked={draft.isUntilNow}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    isUntilNow: e.target.checked,
                    MonthOut: e.target.checked ? '' : draft.MonthOut,
                    YearOut: e.target.checked ? '' : draft.YearOut,
                  })
                }
              />
              <label htmlFor="isUntilNow">Until Now</label>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex justify-content-end">
          <button type="button" id="btnAddEducation" className="btn btn-secondary px-4" onClick={add}>
            Add Education
          </button>
        </div>
        </form>
      </Modal>
    </>
  )
}
